import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { message, Row, Col } from "antd";
import { CreditCardOutlined, HistoryOutlined } from "@ant-design/icons";
import { useCurrentUser } from "../actions/auth";
import {
    useCreateSubscription,
    useVerifyPayment,
    useCreateTrialSubscription,
    useCancelSubscription,
    useRestartSubscription,
    usePauseSubscription,
    useResumeSubscription,
    useUpgradeSubscription,
    useVerifyOneTimePayment,
    usePreviewSubscriptionPrice,
} from "../_actions/mutations/subscription";
import { useRazorpay } from "../payments/QurealPay";
import QurealSection from "../common/QurealSection";
import SectionHeader from "../components/commons/SectionHeader";
import PricingModal from "../components/PricingModal";
import SubscriptionManager from "./components/SubscriptionManager";
import TransactionHistory from "./components/TransactionHistory";
import BillingReviewSection from "./components/BillingReviewSection";
import TokenTopUpCard from "./components/TokenTopUpCard";

const BillingPage = () => {
    const history = useHistory();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [reviewPlan, setReviewPlan] = useState(null);
    const [upgradingSubscriptionId, setUpgradingSubscriptionId] =
        useState(null);
    const [isActivating, setIsActivating] = useState(false);
    const { data: currentUser } = useCurrentUser();
    const orgId = currentUser?.selected_org?.id;
    const [previewData, setPreviewData] = useState(null);
    const [couponError, setCouponError] = useState(null);
    const [paymentCancelled, setPaymentCancelled] = useState(false);
    // getting the error message from the coupon
    const getCouponErrorMessage = (error) => {
        const detail = error?.response?.data?.detail;
        if (!detail) {
            return error?.response?.data?.message || "Invalid coupon code";
        }
        if (detail.includes("Invalid coupon")) {
            const code = detail.split(": ")[1]?.trim();
            return code
                ? `The coupon '${code}' is not valid`
                : "The coupon code is not valid";
        }
        if (detail.toLowerCase().includes("already used")) {
            return "You have already used this coupon";
        }
        if (detail.toLowerCase().includes("expired")) {
            return "This coupon code has expired";
        }
        return detail;
    };
    const resetCouponState = () => {
        setPreviewData(null);
        setCouponError(null);
    };
    // previewing the price
    const previewPrice = usePreviewSubscriptionPrice(
        (data) => {
            setPreviewData(data);
            setCouponError(null);
            message.success("Coupon applied successfully!");
        },
        (error) => {
            resetCouponState();
            setCouponError(getCouponErrorMessage(error));
        }
    );
    // applying a coupon
    const handleApplyCoupon = (code, onSuccess) => {
        if (!reviewPlan || !orgId) return;

        const currentCodes = previewData?.accepted_coupon_codes || [];
        if (currentCodes.includes(code)) {
            setCouponError("Coupon already applied");
            return;
        }

        setCouponError(null);
        previewPrice.mutate(
            {
                plan_id: reviewPlan.id,
                organization_id: orgId,
                selected_coupon_codes: [...currentCodes, code],
            },
            {
                onSuccess: () => {
                    if (onSuccess) onSuccess();
                },
            }
        );
    };
    // removing a coupon
    const handleRemoveCoupon = (codeToRemove) => {
        const currentCodes = previewData?.accepted_coupon_codes || [];
        const newCodes = currentCodes.filter((c) => c !== codeToRemove);

        if (newCodes.length === 0) {
            resetCouponState();
        } else {
            previewPrice.mutate({
                plan_id: reviewPlan.id,
                organization_id: orgId,
                selected_coupon_codes: newCodes,
            });
        }
    };

    // creating a subscription
    const createSubscription = useCreateSubscription(
        (data) => {
            handlePayment(data);
        },
        (error) => {
            message.error(
                error?.response?.data?.message ||
                    "Failed to create subscription"
            );
        }
    );
    const createTrialSubscription = useCreateTrialSubscription(
        (data) => {
            handlePayment(data);
        },
        (error) => {
            message.error(
                error?.response?.data?.message ||
                    "Failed to create trial subscription"
            );
        }
    );
    const subscriptionVerifier = useVerifyPayment(
        (verifyData) => {
            console.log("Payment Verified", verifyData);
        },
        (error) => {
            message.error(
                error?.response?.data?.message || "Payment verification failed"
            );
        }
    );
    const oneTimePaymentVerifier = useVerifyOneTimePayment(
        (verifyData) => {
            console.log("One Time Payment Verified", verifyData);
        },
        (error) => {
            message.error(
                error?.response?.data?.message || "Payment verification failed"
            );
        }
    );

    const cancelSubscription = useCancelSubscription(
        (data) => {
            console.log("Subscription Cancelled", data);
            message.success("Subscription cancelled successfully!");
        },
        (error) => {
            message.error(
                error?.response?.data?.message ||
                    "Failed to cancel subscription"
            );
        }
    );

    const restartSubscription = useRestartSubscription(
        (data) => {
            console.log("Subscription Restarted", data);
            message.success("Subscription restarted successfully!");
        },
        (error) => {
            message.error(
                error?.response?.data?.message ||
                    "Failed to restart subscription"
            );
        }
    );
    const pauseSubscription = usePauseSubscription(
        (data) => {
            console.log("Subscription Paused", data);
            message.success("Subscription paused successfully!");
        },
        (error) => {
            message.error(
                error?.response?.data?.message || "Failed to pause subscription"
            );
        }
    );

    const resumeSubscription = useResumeSubscription(
        (data) => {
            console.log("Subscription Resumed", data);
            message.success("Subscription resumed successfully!");
        },
        (error) => {
            message.error(
                error?.response?.data?.message ||
                    "Failed to resume subscription"
            );
        }
    );

    const upgradeSubscription = useUpgradeSubscription(
        (data) => {
            console.log("Subscription Upgraded", data);
            message.success(
                "Plan upgraded successfully! Your new plan will be activated from the next billing cycle."
            );
        },
        (error) => {
            message.error(
                error?.response?.data?.message ||
                    "Failed to upgrade subscription"
            );
        }
    );

    const { paySubscription } = useRazorpay(
        (response) => {
            message.success("Subscription Activated Successfully!");
            setIsActivating(true);
            setReviewPlan(null);
            resetCouponState();
        },
        (verifyData) => {
            console.log("Payment Verified", verifyData);
        },
        subscriptionVerifier,
        () => {
            setPaymentCancelled(true);
        }
    );

    const { payOrder: payOneTimeOrder } = useRazorpay(
        (response) => {
            message.success("Payment Successful! Verifying...");
            setIsActivating(true);
            setReviewPlan(null);
        },
        (verifyData) => {
            console.log("One Time Payment Verified", verifyData);
        },
        oneTimePaymentVerifier,
        () => {
            setPaymentCancelled(true);
        }
    );

    const [paymentType, setPaymentType] = useState("new"); // 'new', 'trial', 'upgrade'
    const handleOrder = (plan) => {
        if (!orgId) {
            message.error("Organization ID not found!");
            return;
        }
        if (!currentUser?.id) {
            message.error("Account ID not found!");
            return;
        } else {
            setPaymentType("one_time");
            setReviewPlan(plan);
            setIsModalOpen(false);
        }
    };

    const handleSubscribe = (plan) => {
        if (!orgId) {
            message.error("Organization ID not found!");
            return;
        }
        if (!currentUser?.id) {
            message.error("Account ID not found!");
            return;
        }

        // Check if this is an upgrade
        if (upgradingSubscriptionId) {
            setPaymentType("upgrade");
            setReviewPlan(plan);
            setIsModalOpen(false);
        } else {
            // New Subscription
            setPaymentType("new");
            setReviewPlan(plan);
            setIsModalOpen(false);
        }
    };

    const handleProceedPayment = () => {
        if (!reviewPlan || !orgId) return;

        if (!currentUser?.id) {
            message.error("Account ID not found!");
            return;
        }

        // Reset cancelled state when retrying
        setPaymentCancelled(false);

        if (paymentType === "upgrade" && upgradingSubscriptionId) {
            upgradeSubscription.mutate({
                subscription_id: upgradingSubscriptionId,
                new_plan_id: reviewPlan.id,
            });
            setUpgradingSubscriptionId(null);
        } else if (paymentType === "trial") {
            createTrialSubscription.mutate({
                plan_id: reviewPlan.id,
                organization_id: orgId,
                start_immediately: true,
                account_id: currentUser?.id,
                start_at: new Date().toISOString(),
            });
        } else if (paymentType === "one_time") {
            createSubscription.mutate({
                plan_id: reviewPlan.id,
                organization_id: orgId,
                start_immediately: true,
                account_id: currentUser?.id,
                mode: "one_time",
            });
        } else {
            // Default: New Subscription
            createSubscription.mutate({
                plan_id: reviewPlan.id,
                organization_id: orgId,
                start_immediately: true,
                account_id: currentUser?.id,
                mode: "autopay",
                selected_coupon_codes: previewData?.accepted_coupon_codes || [],
            });
        }
    };

    const handleStartTrial = (plan) => {
        if (!orgId) {
            message.error("Organization ID not found!");
            return;
        }
        if (!currentUser?.id) {
            message.error("Account ID not found!");
            return;
        }

        if (currentUser?.selected_org?.trial_used) {
            message.error("Trial already used for this organization.");
            return;
        }

        console.log("Showing review section for trial:", plan);
        setPaymentType("trial");
        setReviewPlan(plan);
        setIsModalOpen(false);
    };
    // handling the payment
    const handlePayment = (subscriptionData) => {
        console.log("handlePayment called with:", subscriptionData);

        if (
            subscriptionData?.mode === "one_time" &&
            subscriptionData?.order_id
        ) {
            payOneTimeOrder({
                order_id: subscriptionData.order_id,
                amount: subscriptionData.payable_amount,
            });
        } else if (subscriptionData?.payment_gateway_subscription_id) {
            paySubscription({
                subscription_id:
                    subscriptionData.payment_gateway_subscription_id,
            });
        } else {
            message.error("Failed to initialize payment gateway.");
        }
    };

    const handleCancel = (subscriptionId) => {
        cancelSubscription.mutate({
            subscription_id: subscriptionId,
            cancel_immediately_with_refund_check: false,
        });
    };

    const handlePause = (subscriptionId) => {
        pauseSubscription.mutate({
            subscription_id: subscriptionId,
        });
    };

    const handleResume = (subscriptionId) => {
        resumeSubscription.mutate({
            subscription_id: subscriptionId,
        });
    };

    const handleUpgrade = (subscriptionId) => {
        // Store the subscription ID and open pricing modal
        setUpgradingSubscriptionId(subscriptionId);
        setIsModalOpen(true);
    };
    const handleRestart = (subscriptionId) => {
        restartSubscription.mutate({
            subscription_id: subscriptionId,
        });
    };

    // Conditional Render: Review Mode
    if (reviewPlan) {
        return (
            <BillingReviewSection
                plan={reviewPlan}
                organization={currentUser?.selected_org}
                user={currentUser}
                paymentType={paymentType}
                onBack={() => {
                    setReviewPlan(null);
                    resetCouponState();
                    setPaymentCancelled(false);
                }}
                onProceed={handleProceedPayment}
                loading={
                    createTrialSubscription.isLoading ||
                    upgradeSubscription.isLoading ||
                    createSubscription.isLoading
                }
                onApplyCoupon={handleApplyCoupon}
                onRemoveCoupon={handleRemoveCoupon}
                previewData={previewData}
                couponError={couponError}
                applyingCoupon={previewPrice.isLoading}
                paymentCancelled={paymentCancelled}
                onChangePlan={(newPlan, type) => {
                    setReviewPlan(newPlan);
                    setPaymentType(type);
                    resetCouponState();
                }}
            />
        );
    }

    return (
        <>
            {/* Subscription + Token Top-Up Section */}
            <QurealSection
                header={
                    <SectionHeader
                        heading="Billing & Subscription"
                        icon={<CreditCardOutlined />}
                    />
                }
                wrapper_style={{ minHeight: "auto", marginBottom: "20px" }}
                footer={false}
            >
                <div style={{ padding: "20px 24px" }}>
                    <Row gutter={[24, 24]}>
                        <Col xs={24} lg={14}>
                            <SubscriptionManager
                                orgId={orgId}
                                onSubscribe={() => setIsModalOpen(true)}
                                onCancel={handleCancel}
                                onPause={handlePause}
                                onResume={handleResume}
                                onUpgrade={handleUpgrade}
                                onRestart={handleRestart}
                                loadingStates={{
                                    cancel: cancelSubscription.isLoading,
                                    pause: pauseSubscription.isLoading,
                                    resume: resumeSubscription.isLoading,
                                    upgrade: upgradeSubscription.isLoading,
                                }}
                                isActivating={isActivating}
                            />
                        </Col>
                        <Col xs={24} lg={10}>
                            <TokenTopUpCard
                                orgId={orgId}
                                userId={currentUser?.id}
                            />
                        </Col>
                    </Row>
                </div>
            </QurealSection>

            {/* Transaction History Section */}
            <QurealSection
                header={
                    <SectionHeader
                        heading="Transaction History"
                        icon={<HistoryOutlined />}
                    />
                }
                footer={false}
                wrapper_style={{ minHeight: "auto" }}
            >
                <TransactionHistory organizationId={orgId} />
            </QurealSection>

            {/* Pricing Modal */}
            <PricingModal
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSubscribe={handleSubscribe}
                onOneTimePurchase={handleOrder}
                onStartTrial={handleStartTrial}
                loading={
                    createSubscription.isLoading ||
                    createTrialSubscription.isLoading
                }
            />
        </>
    );
};

export default BillingPage;
